/**
 * Fixed (non-random) datasets for the BudgetOS calculation test suite.
 * Expected values are hardcoded from independent spreadsheet math.
 */

import type {
  Category,
  Debt,
  NetWorthLog,
  SinkingFund,
  Transaction,
} from "../schema";

export const FIXTURE_YEAR = 2026;

export const FIXTURE_CATEGORIES: Category[] = [
  { id: "cat-sal", name: "Salary", type: "income", group: "income", monthlyTarget: 5000 },
  { id: "cat-side", name: "Side", type: "income", group: "income", monthlyTarget: 500 },
  { id: "cat-rent", name: "Rent", type: "expense", group: "needs", monthlyTarget: 1500 },
  { id: "cat-groc", name: "Groceries", type: "expense", group: "needs", monthlyTarget: 400 },
  { id: "cat-dine", name: "Dining", type: "expense", group: "wants", monthlyTarget: 200 },
  { id: "cat-save", name: "Emergency", type: "expense", group: "savings", monthlyTarget: 300 },
];

/** Empty state */
export const DS_EMPTY = {
  id: "empty",
  categories: FIXTURE_CATEGORIES,
  txs: [] as Transaction[],
  debts: [] as Debt[],
  funds: [] as SinkingFund[],
  nwLogs: [] as NetWorthLog[],
};

/**
 * Single month May 2026:
 * Income: 5000 salary
 * Expenses: rent 1500, dine 200, save 300 → total expense 2000
 * Leftover 3000, savings rate 60%, actualSaved 300
 * Needs 1500 (30%), Wants 200 (4%), Savings 300 (6%)
 */
export const DS_SINGLE_MONTH: Transaction[] = [
  { id: "t1", date: "2026-05-01", type: "income", amount: 5000, categoryId: "cat-sal", accountId: "a1", person: "Household" },
  { id: "t2", date: "2026-05-01", type: "expense", amount: 1500, categoryId: "cat-rent", accountId: "a1", person: "Household" },
  { id: "t3", date: "2026-05-15", type: "expense", amount: 200, categoryId: "cat-dine", accountId: "a1", person: "Household" },
  { id: "t4", date: "2026-05-20", type: "expense", amount: 300, categoryId: "cat-save", accountId: "a1", person: "Household" },
];

/**
 * Negative month June 2026: income 1000, expenses 2500 → leftover -1500, rate -150%
 */
export const DS_NEGATIVE_MONTH: Transaction[] = [
  { id: "n1", date: "2026-06-01", type: "income", amount: 1000, categoryId: "cat-sal", accountId: "a1", person: "Household" },
  { id: "n2", date: "2026-06-02", type: "expense", amount: 1500, categoryId: "cat-rent", accountId: "a1", person: "Household" },
  { id: "n3", date: "2026-06-10", type: "expense", amount: 1000, categoryId: "cat-groc", accountId: "a1", person: "Household" },
];

/**
 * Full year: each month identical for easy hand-calc
 * Per month: income 5500 (5000+500), expenses rent1500+groc400+dine200+save300 = 2400
 * Year income 66000, expense 28800, leftover 37200, rate 56.3636...%
 * actualSaved = 300*12 = 3600, totalSavedTarget = 300*12 = 3600
 */
export function buildFullYearTxs(): Transaction[] {
  const txs: Transaction[] = [];
  for (let m = 1; m <= 12; m++) {
    const mm = String(m).padStart(2, "0");
    txs.push(
      { id: `fy-sal-${m}`, date: `2026-${mm}-01`, type: "income", amount: 5000, categoryId: "cat-sal", accountId: "a1", person: "Household" },
      { id: `fy-side-${m}`, date: `2026-${mm}-05`, type: "income", amount: 500, categoryId: "cat-side", accountId: "a1", person: "Side Hustle" },
      { id: `fy-rent-${m}`, date: `2026-${mm}-01`, type: "expense", amount: 1500, categoryId: "cat-rent", accountId: "a1", person: "Household" },
      { id: `fy-groc-${m}`, date: `2026-${mm}-10`, type: "expense", amount: 400, categoryId: "cat-groc", accountId: "a1", person: "Household" },
      { id: `fy-dine-${m}`, date: `2026-${mm}-15`, type: "expense", amount: 200, categoryId: "cat-dine", accountId: "a1", person: "Household" },
      { id: `fy-save-${m}`, date: `2026-${mm}-20`, type: "expense", amount: 300, categoryId: "cat-save", accountId: "a1", person: "Household" }
    );
  }
  return txs;
}

/** Simple debts for deterministic amortization from a fixed start date */
export const DS_DEBTS: Debt[] = [
  { id: "d1", name: "Card", balance: 1000, interestRate: 12, minPayment: 100, extraPayment: 50 },
  { id: "d2", name: "Loan", balance: 5000, interestRate: 6, minPayment: 200, extraPayment: 0 },
];

/** Sinking funds with known targets relative to asOf = 2026-01-01 */
export const DS_FUNDS: SinkingFund[] = [
  { id: "f0", name: "Zero", targetAmount: 1000, targetDate: "2026-07-01", currentSaved: 0 },
  { id: "f1", name: "Half", targetAmount: 2000, targetDate: "2026-07-01", currentSaved: 1000 },
  { id: "f2", name: "Full", targetAmount: 500, targetDate: "2026-07-01", currentSaved: 500 },
  { id: "f3", name: "Over", targetAmount: 500, targetDate: "2026-07-01", currentSaved: 750 },
];

export const DS_NW_LOGS: NetWorthLog[] = [
  { id: "nw1", monthYear: "2026-01", assetsTotal: 100000, debtsTotal: 40000 },
  { id: "nw2", monthYear: "2026-02", assetsTotal: 105000, debtsTotal: 38000 },
];

/** Legacy v1-shaped backup (missing optional fields) for migration/import parity checks */
export const DS_LEGACY_BACKUP = {
  __version: 1,
  __exportedAt: "2025-01-01T00:00:00.000Z",
  tables: {
    transactions: [
      { id: "legacy1", date: "2026-05-01", type: "income", amount: 5000, categoryId: "cat-sal", accountId: "a1", person: "Household" },
      { id: "legacy2", date: "2026-05-01", type: "expense", amount: 1500, categoryId: "cat-rent", accountId: "a1", person: "Household" },
    ],
    categories: FIXTURE_CATEGORIES,
  },
};
